import { useQuery } from '@tanstack/react-query'
import { baseApiService, type User, type CurrentUserResponse } from './api'
import {
  authApiService,
  type UserSettings,
  type UserPolicies,
  type BrowserFolder,
  type IntegrationsResponse
} from './api/auth'
import { toast } from './toast'
import { getAllowedLanguages, type ProcessedLanguageInfo } from './language'
import { initService } from './init.service'

// Extended User interface with contentForApprovalFolderId
export interface UserWithApprovalFolder extends User {
  contentForApprovalFolderId?: number
}

// Login response interface
export interface LoginResponse {
  user: UserWithApprovalFolder
  token: string | null
}

// User role constants
export const USER_ROLES = {
  USER: 'USER',
  ADMIN: 'ADMIN',
  CLOUD_ADMIN_ROLE: 'CLOUD_ADMIN_ROLE',
  EOS_SITE_USER: 'EOS_SITE_USER',
  ETMF_MANAGER: 'ETMF_MANAGER',
  ETMF_SPECIALIST: 'ETMF_SPECIALIST',
  ETMF_SITE_USER: 'ETMF_SITE_USER',
  ETMF_STUDY_USER: 'ETMF_STUDY_USER',
  ETMF_SPONSOR: 'ETMF_SPONSOR',
  RCR_USER: 'RCR_USER'
}

// User permission constants
export const USER_PERMISSIONS = {
  manage_etmf: 'manage_etmf',
  manage_study_etmf: 'manage_study_etmf',
  upload_site_artifact: 'upload_site_artifact',
  access_study_staging_area: 'access_study_staging_area',
  view_study_etmf: 'view_study_etmf',
  access_rave_content_repository: 'access_rave_content_repository',
  full_rcr_document: 'full_rcr_document',
  read_rcr_document: 'read_rcr_document'
}

// System folder type constants
export const SYSTEM_FOLDER_TYPES = {
  FORMS: 'FORMS',
  TEMPLATES: 'TEMPLATES',
  REGULATED_CONTENT: 'REGULATED_CONTENT',
  WORKFLOWS: 'WORKFLOWS',
  INIT_ETMF_FILES: 'INIT_ETMF_FILES',
  REPORTS: 'REPORTS'
}

export interface AuthState {
  user: User | null
  token: string | null
  lang: string
  role: string
  expiresAt: number | null
  policies: UserPolicies
  integrations: IntegrationsResponse
  browser: Record<string, BrowserFolder>
  possibleLanguages: ProcessedLanguageInfo[]
  contentForApprovalFolderId?: number
}

export const getCookie = (name: string): string | null => {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() ?? null
  return null
}

export const setCookie = (name: string, value: string): void => {
  document.cookie = `${name}=${value}; path=/; SameSite=Lax`
}

export const getAuthHeaders = (): Record<string, string> => {
  return {
    'X-Requested-With': 'XMLHttpRequest',
    Cookie: document.cookie
  }
}

export const getTenantUuid = (): string | null => {
  const urlParams = new URLSearchParams(window.location.search)
  const tenantFromUrl = urlParams.get('tenantUuid')

  if (tenantFromUrl) {
    localStorage.setItem('tenantUuid', tenantFromUrl)
    return tenantFromUrl
  }

  return localStorage.getItem('tenantUuid')
}

export const isLoggedIn = (): boolean => {
  const authInfo = localStorage.getItem('authInfo')
  if (!authInfo) return false

  try {
    const parsed = JSON.parse(authInfo)
    return !!(parsed.user && parsed.token)
  } catch (e) {
    console.error('Error parsing authInfo:', e)
    return false
  }
}

export const getStoredAuthInfo = (): AuthState | null => {
  const authInfo = localStorage.getItem('authInfo')
  if (!authInfo) return null

  try {
    return JSON.parse(authInfo) as AuthState
  } catch (e) {
    console.error('Error parsing authInfo:', e)
    return null
  }
}

export const storeAuthInfo = (authInfo: AuthState): void => {
  localStorage.setItem('authInfo', JSON.stringify(authInfo))
  if (authInfo.lang) {
    setCookie('appLanguage', authInfo.lang)
  }
}

export const fetchCurrentUser = async (): Promise<User> => {
  const response = await baseApiService.fetch<CurrentUserResponse>('/api/currentUser')

  // Safely access contentForApprovalFolderId with optional chaining
  const userWithApprovalFolder = response.user as UserWithApprovalFolder

  const authInfo: AuthState = {
    user: response.user,
    token: response.token ?? 'cookiebased',
    lang: response.user.locale,
    role: response.user.role,
    expiresAt: null,
    policies: {},
    integrations: {},
    browser: {},
    possibleLanguages: [],
    contentForApprovalFolderId: userWithApprovalFolder.contentForApprovalFolderId ?? 0
  }

  storeAuthInfo(authInfo)

  // Store tenantUuid for development API calls
  if (response.user.tenantUuid) {
    localStorage.setItem('tenantUuid', response.user.tenantUuid)
  }

  // Load policies, browser folders, integrations, and languages in background
  refreshUserPolicies()

  return response.user
}

export const getUser = (): User | null => {
  const authInfo = getStoredAuthInfo()
  return authInfo?.user ?? null
}

export const getUserId = (): number | null => {
  const user = getUser()
  return user?.id ?? null
}

export const getToken = (): string | null => {
  const authInfo = getStoredAuthInfo()
  return authInfo?.token ?? null
}

export const getRole = (): string | null => {
  const authInfo = getStoredAuthInfo()
  if (!authInfo?.user || !authInfo.role) return null

  // Check if EOS is enabled and user is a basic USER
  // TODO-EOS: Replace with actual features service when available
  // const featuresInfo = service.init.getFeaturesInfo()
  // const isEosEnabled = featuresInfo?.eosFeature?.show
  const isEosEnabled = initService.getFeaturesInfo().eosFeature.show

  if (authInfo.role === USER_ROLES.USER && isEosEnabled) {
    return USER_ROLES.EOS_SITE_USER
  }

  // Handle CLOUD_ADMIN_ROLE with permission-based role determination
  if (authInfo.role === USER_ROLES.CLOUD_ADMIN_ROLE && Array.isArray(authInfo.user.permissions)) {
    const permissions = authInfo.user.permissions as string[]

    // Check permissions in priority order
    if (permissions.includes(USER_PERMISSIONS.manage_etmf)) {
      return USER_ROLES.ETMF_MANAGER
    } else if (permissions.includes(USER_PERMISSIONS.manage_study_etmf)) {
      return USER_ROLES.ETMF_SPECIALIST
    } else if (permissions.includes(USER_PERMISSIONS.upload_site_artifact)) {
      return USER_ROLES.ETMF_SITE_USER
    } else if (permissions.includes(USER_PERMISSIONS.access_study_staging_area)) {
      return USER_ROLES.ETMF_STUDY_USER
    } else if (permissions.includes(USER_PERMISSIONS.view_study_etmf)) {
      return USER_ROLES.ETMF_SPONSOR
    } else if (
      permissions.some((perm: string) =>
        [
          USER_PERMISSIONS.access_rave_content_repository,
          USER_PERMISSIONS.full_rcr_document,
          USER_PERMISSIONS.read_rcr_document
        ].includes(perm)
      )
    ) {
      return USER_ROLES.RCR_USER
    }
  }

  // Return the base role if no special conditions apply
  return authInfo.role
}

export const isAuthorized = (rolesToCheck: string | string[]): boolean => {
  if (!Array.isArray(rolesToCheck)) {
    rolesToCheck = [rolesToCheck]
  }

  return isLoggedIn() && rolesToCheck.includes(getRole() || '')
}

export const getDateFormat = (): string => {
  const user = getUser()
  return user?.dateFormat ?? 'yyyy/MM/dd'
}

export const getTimeFormat = (): string => {
  const user = getUser()
  if (!user?.timeFormat) return 'h:mm:ss a'

  const format12h = `h${user.timeFormat}mm${user.timeFormat}ss a`
  const format24h = `HH${user.timeFormat}mm${user.timeFormat}ss`
  return user.timeFormat24h ? format24h : format12h
}

export const getReportsTimeFormat = (): string => {
  const user = getUser()
  if (!user?.timeFormat) return 'h:mm:ss:sss a'

  const format12h = `h${user.timeFormat}mm${user.timeFormat}ss${user.timeFormat}sss a`
  const format24h = `HH${user.timeFormat}mm${user.timeFormat}ss${user.timeFormat}sss`
  return user.timeFormat24h ? format24h : format12h
}

export const getCurrentLang = (): string => {
  return getCookie('appLanguage') ?? getStoredAuthInfo()?.lang ?? 'en-US'
}

export const getActiveLanguage = (): string => {
  const authInfo = getStoredAuthInfo()
  return authInfo?.lang ?? 'en-US'
}

export const changeLanguage = (lang: string): void => {
  const authInfo = getStoredAuthInfo()
  if (authInfo) {
    authInfo.lang = lang
    storeAuthInfo(authInfo)
  }
}

export const clearAuthInfo = (): void => {
  const authInfo: AuthState = {
    user: null,
    token: null,
    lang: 'en-US',
    role: '',
    expiresAt: null,
    policies: {},
    integrations: {},
    browser: {},
    possibleLanguages: [],
    contentForApprovalFolderId: 0
  }
  storeAuthInfo(authInfo)
}

/**
 * Handle tenantUuid change - implements legacy setTenantUuid logic
 * Matches behavior from public.layout.stateProvider.js
 *
 * When tenantUuid changes:
 * 1. Clear auth info
 * 2. Store new tenantUuid
 * 3. Try to get current user with new tenant
 * 4. If fails, logout and clear everything
 */
export const handleTenantUuidChange = async (newTenantUuid: string): Promise<void> => {
  const currentTenantUuid = getTenant()

  // If no current tenant or tenant hasn't changed, just store it
  if (!currentTenantUuid || currentTenantUuid === newTenantUuid) {
    localStorage.setItem('tenantUuid', newTenantUuid)
    return
  }

  // Tenant is changing - clear cached data
  console.log(`Tenant changing from ${currentTenantUuid} to ${newTenantUuid}`)
  clearAuthInfo()

  // Store new tenant
  localStorage.setItem('tenantUuid', newTenantUuid)

  try {
    // Try to get current logged in user with new tenant
    await fetchCurrentUser()
    console.log('Successfully switched tenant')
  } catch (error) {
    // Failed to get user - invalidate old session
    console.log('Failed to get user with new tenant, logging out')
    try {
      await logout()
    } catch (logoutError) {
      console.error('Logout failed during tenant switch:', logoutError)
    }
    // Clear auth info again to ensure clean state
    clearAuthInfo()
    // Store tenantUuid again after logout
    localStorage.setItem('tenantUuid', newTenantUuid)
  }
}

export const logout = async (): Promise<boolean> => {
  try {
    const result = await authApiService.logout()
    clearAuthInfo()
    // If API returns a redirect URL, navigate there (legacy parity)
    if (typeof result === 'string' && result) {
      window.location.href = result
      return true
    }
    if (result && typeof result === 'object' && 'url' in result && typeof result.url === 'string' && result.url) {
      window.location.href = result.url
      return true
    }
    return true
  } catch (error) {
    console.error('Logout error:', error)
    clearAuthInfo() // Clear auth info even if API call fails
    return false
  }
}

export const useCurrentUser = () => {
  return useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  })
}

export const getCurrentUserData = () => getUser()

export const getAuthType = () => {
  const user = getUser()
  return user?.authType
}

export const getPolicies = (): Record<string, unknown> => {
  const authInfo = getStoredAuthInfo()
  return authInfo?.policies ?? {}
}

export const getTenant = (): string | null => {
  const user = getUser()
  return user?.tenantUuid ?? null
}

export const getIntegrations = (): Record<string, unknown> => {
  const authInfo = getStoredAuthInfo()
  return authInfo?.integrations ?? {}
}

export const getBrowser = (
  systemType?: string
): BrowserFolder | Record<string, BrowserFolder> | Record<string, unknown> => {
  const authInfo = getStoredAuthInfo()
  if (!authInfo?.browser) return {}

  if (!systemType) {
    return authInfo.browser
  }

  return authInfo.browser[systemType] ?? {}
}

export const getExternalEditorName = (): string | null => {
  const integrations = getIntegrations()
  const externalEditor = integrations.externalEditor as string

  switch (externalEditor) {
    case 'OFFICE':
      return 'INTEGRATION_EDITOR_OFFICE_365'
    case 'GDRIVE':
      return 'INTEGRATION_EDITOR_GOOGLE_DRIVE'
    default:
      return null
  }
}

export const requireESignatureForFileCommands = (): boolean => {
  const policies = getPolicies()
  return !!policies.requireESignatureForFileCommands
}

export const requireUsernameInESignature = (): boolean => {
  const policies = getPolicies()
  return !!policies.requireUsernameInESignature
}

export const approverMustViewDocumentPriorToApproval = (): boolean => {
  const policies = getPolicies()
  return !!policies.approverMustViewDocumentPriorToApproval
}

export const raUserMustViewDocumentPriorToApproval = (): boolean => {
  const policies = getPolicies()
  return !!policies.raUserMustViewDocumentPriorToApproval
}

export const getUserRoles = () => {
  return [getRole()]
}

export const isRcrUser = (): boolean => {
  const rcrRoles = [USER_ROLES.RCR_USER]
  const userRole = getRole()
  return !!userRole && rcrRoles.includes(userRole)
}

export const isStagingUser = (): boolean => {
  const stagingRoles = [
    USER_ROLES.ETMF_MANAGER,
    USER_ROLES.ETMF_SPECIALIST,
    USER_ROLES.ETMF_STUDY_USER,
    USER_ROLES.ETMF_SITE_USER,
    USER_ROLES.ETMF_SPONSOR
  ]
  const userRole = getRole()
  return !!userRole && stagingRoles.includes(userRole)
}

export const isStagingUploader = (): boolean => {
  const stagingRoles = [USER_ROLES.ETMF_STUDY_USER, USER_ROLES.ETMF_SITE_USER]
  const userRole = getRole()
  return !!userRole && stagingRoles.includes(userRole)
}

export const updateUserSettings = async (newSettings: UserSettings, hideLoader?: boolean): Promise<void> => {
  await authApiService.changeUserSettings(newSettings)
  await updateUserInfo()
}

export const updateUserInfo = async (): Promise<void> => {
  const response = await authApiService.getCurrentLoggedInUser()
  const authInfo = getStoredAuthInfo()

  if (authInfo && response.user) {
    authInfo.user = response.user
    authInfo.role = response.user.role
    storeAuthInfo(authInfo)
  }
}

const getAndSetBrowserFolders = async (): Promise<Record<string, BrowserFolder>> => {
  try {
    const response = await authApiService.getBrowserFolders()
    const data = response.elements

    if (data.length === 0) {
      console.warn('No browser folders found in response')
      return {}
    }
    // Return the browser object instead of storing it
    // This prevents race condition in refreshUserPolicies
    return {
      [SYSTEM_FOLDER_TYPES.FORMS]:
        data.find((f) => f.systemFolderType === SYSTEM_FOLDER_TYPES.FORMS) || ({} as BrowserFolder),
      [SYSTEM_FOLDER_TYPES.TEMPLATES]:
        data.find((f) => f.systemFolderType === SYSTEM_FOLDER_TYPES.TEMPLATES) || ({} as BrowserFolder),
      [SYSTEM_FOLDER_TYPES.REGULATED_CONTENT]:
        data.find((f) => f.systemFolderType === SYSTEM_FOLDER_TYPES.REGULATED_CONTENT) || ({} as BrowserFolder),
      [SYSTEM_FOLDER_TYPES.WORKFLOWS]:
        data.find((f) => f.systemFolderType === SYSTEM_FOLDER_TYPES.WORKFLOWS) || ({} as BrowserFolder),
      [SYSTEM_FOLDER_TYPES.INIT_ETMF_FILES]:
        data.find((f) => f.systemFolderType === SYSTEM_FOLDER_TYPES.INIT_ETMF_FILES) || ({} as BrowserFolder),
      [SYSTEM_FOLDER_TYPES.REPORTS]:
        data.find((f) => f.systemFolderType === SYSTEM_FOLDER_TYPES.REPORTS) || ({} as BrowserFolder)
    }
  } catch (error) {
    console.error('Error loading browser folders:', error)
    // Return empty object on error to match legacy behavior
    return {}
  }
}

export const checkCurrentLanguageIsAllowed = (possibleLanguages: ProcessedLanguageInfo[]): boolean => {
  const currentLang = getCurrentLang()
  return possibleLanguages.some((langInfo) => langInfo.id === currentLang)
}

export const refreshUserPolicies = async (finalCallback?: () => void): Promise<void> => {
  try {
    const [policies, browserFolders, integrations, languagesResponse] = await Promise.all([
      authApiService.getUserPolicies(),
      getAndSetBrowserFolders(),
      authApiService.getIntegrations(1),
      getAllowedLanguages()
    ])

    const authInfo = getStoredAuthInfo()
    if (authInfo) {
      authInfo.policies = policies
      authInfo.browser = browserFolders
      authInfo.integrations = integrations
      authInfo.possibleLanguages = languagesResponse || []

      // Check if current language is allowed, otherwise switch to English (default)
      const fallbackLanguage = 'en-US'
      if (!checkCurrentLanguageIsAllowed(authInfo.possibleLanguages)) {
        changeLanguage(fallbackLanguage)
        toast.warning('APP_WARNING_LANGUAGE_IS_NOT_AVAILABLE_CHANGED_TO_DEFAULT')
      }

      storeAuthInfo(authInfo)
    }

    if (finalCallback) {
      finalCallback()
    }
  } catch (error) {
    console.error('Error refreshing user policies:', error)
    if (finalCallback) {
      finalCallback()
    }
  }
}

export const checkPassword = async (password: string): Promise<boolean> => {
  const user = getUser()

  if (!user?.email || !password) {
    return false
  }

  try {
    await authApiService.checkPassword(password)
    return true
  } catch (error) {
    console.error('Password check failed:', error)
    return false
  }
}

export const login = (email: string, password: string) => {
  if (!email || !password) {
    return
  }
  return authApiService.login(email, password).then(
    (data) => {
      // Type the login response properly
      const loginResponse = data as LoginResponse
      postprocessLogin(loginResponse)
    },
    (error) => toast.error(error?.data?.message || 'Login failed')
  )
}

const postprocessLogin = (response: LoginResponse) => {
  const authInfo: AuthState = {
    user: response.user,
    token: response.token ?? 'cookiebased',
    lang: response.user?.locale || 'en-US',
    role: response.user?.role || '',
    expiresAt: null,
    policies: {},
    integrations: {},
    browser: {},
    possibleLanguages: [],
    contentForApprovalFolderId: response.user?.contentForApprovalFolderId ?? 0
  }

  storeAuthInfo(authInfo)

  // Store tenantUuid for development API calls
  if (response.user?.tenantUuid) {
    localStorage.setItem('tenantUuid', response.user.tenantUuid)
  }

  // Load policies, browser folders, integrations, and languages
  refreshUserPolicies()
}

export const useAuth = () => {
  const { data: user, isLoading, error } = useCurrentUser()

  return {
    user,
    isLoading,
    error,
    isLoggedIn: !!user,
    getUserId,
    getUser,
    getToken,
    getRole,
    getDateFormat,
    getTimeFormat,
    getReportsTimeFormat,
    getCurrentLang,
    getActiveLanguage,
    changeLanguage,
    isAuthorized,
    logout,
    clearAuthInfo,
    handleTenantUuidChange,
    getPolicies,
    getTenant,
    getIntegrations,
    getBrowser,
    getExternalEditorName,
    requireESignatureForFileCommands,
    requireUsernameInESignature,
    approverMustViewDocumentPriorToApproval,
    raUserMustViewDocumentPriorToApproval,
    getUserRoles,
    isRcrUser,
    isStagingUser,
    isStagingUploader,
    checkPassword,
    updateUserSettings,
    updateUserInfo,
    refreshUserPolicies,
    checkCurrentLanguageIsAllowed
  }
}
